import { enqueueSnackbar } from 'notistack';
import { useState, useCallback } from 'react';

import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Button from '@mui/material/Button';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { ApplicationStage } from 'src/services/applications/applications.types';
import {
  useGetApplicationByIdQuery,
  useProgressCandidateApplicationMutation,
  useRequestApplicationStageChangeMutation,
  useGetOutgoingApplicationResourceRequestsQuery,
} from 'src/services/applications/applications.service';
import {
  willNotifyAnyone,
  getUpdateStageErrorMessage,
  getApplicationNotificationContext,
  getConfirmStageChangeContentMessage,
  getConfirmStageChangeRequestContentMessage,
} from 'src/services/applications/applications.utils';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { INestedMenuItem } from 'src/components/menu/nested-menu';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';
import { useCandidateActions } from 'src/sections/candidates/hooks/use-candidate-actions';
import ShortlistPackDialog from 'src/sections/presentations/modals/shortlist-pack-dialog';
import OffersListModal from 'src/sections/jobs/applications/modals/offer/offers-list-modal';
import ApplicationWithdrawDialog from 'src/sections/jobs/applications/modals/application-withdraw-dialog';
import InterviewListModal from 'src/sections/jobs/applications/modals/interview/interview-list-modal';
import InterviewPhaseModal from 'src/sections/jobs/applications/modals/interview/interview-phase-modal';
import RequestReferenceModal from 'src/sections/jobs/applications/modals/references/reference-request-modal';
import ResourceRequestModal from 'src/sections/jobs/applications/modals/resource-requests/resource-request-modal';
import ReferenceRequestListModal from 'src/sections/jobs/applications/modals/references/reference-request-list-modal';
import ResourceRequestsListModal from 'src/sections/jobs/applications/modals/resource-requests/resource-requests-list-modal';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import ApplicationActionMenu from './application-action-menu';

// ----------------------------------------------------------------------

type Props = {
  applicationId: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'soft';
};

/**
 * The "Application Actions" button, its menu, and every dialog the menu opens. The application
 * profile and the application quick view both render it, so they offer the same actions (JT1-1799).
 *
 * The menu lists the application's actions, then the candidate's own after a divider, so an
 * application profile offers what a candidate profile does. The candidate actions act on the viewing
 * org's OrganizationCandidate: signing requests and meetings are attached to the candidate, not the
 * application. There is no such record for a candidate account, or for a client before a
 * recruiter-managed application is first shortlisted, and then they're left out. The application keeps
 * its own Send Email and Import Profile, so the candidate's versions of those are left out too.
 */
export default function ApplicationActionsButton({ applicationId, size = 'medium', variant = 'contained' }: Props) {
  const { t } = useTranslate();
  const tenantType = useOrgTenant();

  const actionsPopover = usePopover();

  const confirmStageChange = useBoolean();
  const confirmRequestStageChange = useBoolean();
  const infoRequestInitiateModalControl = useBoolean();
  const showOutgoingResourceRequests = useBoolean();
  const referenceInitiateModalControl = useBoolean();
  const referenceRequestListModalControl = useBoolean();
  const interviewListModalControl = useBoolean();
  const showOffersModal = useBoolean();
  const showInterviewPhaseModal = useBoolean();
  const withdrawControl = useBoolean();
  const shortlistPackControl = useBoolean();
  const [packKind, setPackKind] = useState<'shortlist' | 'comparison'>('shortlist');
  const { featureEnabled } = useFeatureFlags();

  const [requestedStage, setRequestedStage] = useState<ApplicationStage | null>(null);

  const [resourceRequestPageOptions, setResourceRequestPageOptions] = useState<PageOptions>({ page: 1, per_page: 5 });
  const onChangeResourceRequestPage = useCallback((_e: unknown, page: number) => {
    setResourceRequestPageOptions((prev) => ({ ...prev, page }));
  }, []);

  const { currentData: application, isLoading } = useGetApplicationByIdQuery(applicationId, { skip: !applicationId });

  const { currentData: resourceRequests } = useGetOutgoingApplicationResourceRequestsQuery(
    { applicationId, params: { ...resourceRequestPageOptions } },
    { skip: !applicationId }
  );

  const [progressApplication, { isLoading: isProgressing }] = useProgressCandidateApplicationMutation();
  const [requestStageChange, { isLoading: isRequesting }] = useRequestApplicationStageChangeMutation();

  const onProgressApplication = useCallback(async (stage: ApplicationStage) => {
    confirmStageChange.onFalse();
    try {
      await progressApplication({ applicationId, data: { stage } }).unwrap();
      enqueueSnackbar(t('applications.api.update_stage.success'));
    } catch (e) {
      enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
    }
  }, [applicationId, confirmStageChange, progressApplication, t]);

  const onRequestProgressApplication = useCallback(async (stage: ApplicationStage) => {
    confirmRequestStageChange.onFalse();
    try {
      await requestStageChange({ applicationId, requested_stage: stage }).unwrap();
      enqueueSnackbar(t('applications.api.update_stage.success'));
    } catch (e) {
      enqueueSnackbar(getUpdateStageErrorMessage(e), { variant: 'error' });
    }
  }, [applicationId, confirmRequestStageChange, requestStageChange, t]);

  // Only ask for confirmation when the move notifies someone; otherwise it goes straight through
  // (JT1-1576).
  const confirmOrProgress = useCallback((stage: ApplicationStage) => {
    setRequestedStage(stage);
    if (willNotifyAnyone(stage, tenantType, getApplicationNotificationContext(application))) {
      confirmStageChange.onTrue();
    } else {
      onProgressApplication(stage);
    }
  }, [application, tenantType, confirmStageChange, onProgressApplication]);

  const orgCandidateId = tenantType !== TenantType.Candidate ? application?.org_candidate?.id : undefined;
  const { actions: candidateActions, modals: candidateActionModals } = useCandidateActions({
    mode: 'record',
    ids: orgCandidateId ? [orgCandidateId] : [],
    target: orgCandidateId ? {
      id: orgCandidateId,
      firstName: application?.attached_profile?.first_name,
      lastName: application?.attached_profile?.last_name,
      email: application?.attached_profile?.email,
      phone: application?.attached_profile?.phone_number,
    } : undefined,
    exclude: ['send-email', 'import-profile'],
  });
  const candidateMenuItems: INestedMenuItem[] = candidateActions
    .filter((action) => !action.hidden)
    .map((action) => ({ label: action.label, caption: action.caption, disabled: action.disabled, onClick: action.onClick }));

  // JT1-1887: this candidate and others on the job in one document, for agencies with the presentation builder.
  const canMakePack = featureEnabled(FeatureType.CANDIDATE_PRESENTATIONS_ENABLED) && tenantType === TenantType.Recruiter && !!orgCandidateId && !!application?.job?.id;
  const menuItems: INestedMenuItem[] = canMakePack
    ? [
      { label: t('presentations.packs.action'), onClick: () => { setPackKind('shortlist'); shortlistPackControl.onTrue(); } },
      { label: t('presentations.packs.compare_action'), onClick: () => { setPackKind('comparison'); shortlistPackControl.onTrue(); } },
      ...candidateMenuItems,
    ]
    : candidateMenuItems;

  return (
    <>
      <LoadingButton
        size={size}
        variant={variant}
        disabled={isLoading || !application}
        loading={isProgressing || isRequesting}
        onClick={actionsPopover.onOpen}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {t('applications.detail.actions.button_text')}
          <Iconify sx={{ display: 'inline-block' }} icon="eva:arrow-ios-downward-fill" />
        </Stack>
      </LoadingButton>

      <CustomPopover open={actionsPopover.open} onClose={actionsPopover.onClose} arrow="top-right">
        <ApplicationActionMenu
          anchorEl={actionsPopover.open}
          applicationId={applicationId}
          closeMenu={actionsPopover.onClose}
          onOpenResourceRequestsModal={infoRequestInitiateModalControl.onTrue}
          onOpenOutgoingResourceRequestsModal={showOutgoingResourceRequests.onTrue}
          onOpenReferenceInitiateModal={referenceInitiateModalControl.onTrue}
          onOpenReferenceRequestListModal={referenceRequestListModalControl.onTrue}
          onOpenInterviewListModal={interviewListModalControl.onTrue}
          onOpenOfferListModal={showOffersModal.onTrue}
          onOpenInterviewPhaseModal={showInterviewPhaseModal.onTrue}
          onOpenRequestStageChangeConfirm={(stage) => {
            setRequestedStage(stage);
            confirmRequestStageChange.onTrue();
          }}
          onOpenStageChangeConfirm={confirmOrProgress}
          onOpenWithdrawDialog={withdrawControl.onTrue}
          candidateItems={menuItems}
        />
      </CustomPopover>

      <ConfirmDialog
        open={confirmRequestStageChange.value}
        onClose={confirmRequestStageChange.onFalse}
        title={t('common.confirm')}
        content={getConfirmStageChangeRequestContentMessage(requestedStage as ApplicationStage)}
        action={
          <Button variant="contained" color="error" onClick={() => onRequestProgressApplication(requestedStage as ApplicationStage)}>
            {t('common.confirm')}
          </Button>
        }
      />

      <ConfirmDialog
        open={confirmStageChange.value}
        onClose={confirmStageChange.onFalse}
        title={t('common.confirm')}
        content={getConfirmStageChangeContentMessage(
          requestedStage as ApplicationStage,
          tenantType,
          getApplicationNotificationContext(application)
        )}
        action={
          <Button variant="contained" color="error" onClick={() => onProgressApplication(requestedStage as ApplicationStage)}>
            {t('common.confirm')}
          </Button>
        }
      />

      <OffersListModal open={showOffersModal.value} applicationId={applicationId} onClose={showOffersModal.onFalse} />

      <ResourceRequestModal
        open={infoRequestInitiateModalControl.value}
        onClose={infoRequestInitiateModalControl.onFalse}
        applicationId={applicationId}
      />

      {application && (
        <ResourceRequestsListModal
          open={showOutgoingResourceRequests.value}
          onClose={showOutgoingResourceRequests.onFalse}
          target="outgoing"
          requests={resourceRequests}
          pageOptions={resourceRequestPageOptions}
          onChangePage={onChangeResourceRequestPage}
          profileId={application.attached_profile.id as string}
        />
      )}

      <InterviewListModal
        applicationId={applicationId}
        open={interviewListModalControl.value}
        onClose={interviewListModalControl.onFalse}
      />

      <ReferenceRequestListModal
        applicationId={applicationId}
        open={referenceRequestListModalControl.value}
        onClose={referenceRequestListModalControl.onFalse}
      />

      <RequestReferenceModal
        applicationId={applicationId}
        open={referenceInitiateModalControl.value}
        onClose={referenceInitiateModalControl.onFalse}
      />

      <InterviewPhaseModal
        open={showInterviewPhaseModal.value}
        onClose={showInterviewPhaseModal.onFalse}
        applicationId={applicationId}
      />

      <ApplicationWithdrawDialog
        open={withdrawControl.value}
        applicationId={applicationId}
        onClose={withdrawControl.onFalse}
        onWithdrawn={withdrawControl.onFalse}
      />

      {candidateActionModals}

      {shortlistPackControl.value && application?.job?.id && (
        <ShortlistPackDialog
          open
          onClose={shortlistPackControl.onFalse}
          jobId={application.job.id}
          jobTitle={application.job.title}
          initialApplicationId={applicationId}
          initialKind={packKind}
        />
      )}
    </>
  );
}
